import {
  Card, Text, Badge, Group, Stack, Loader, Alert, Grid, ThemeIcon, Title, Divider,
} from '@/components/ui';
import { IconAlertCircle, IconSparkles, IconPhoto, IconMicrophone, IconFileText } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useAiWorkflowPresetService } from '@actschurch/ccms-sdk/react';
import { useAiWorkflowStore } from '../../../../stores/aiWorkflowStore';

const inputTypeBadgeColor: Record<string, string> = {
  Image: 'blue',
  Audio: 'orange',
  Text: 'green',
};

const inputTypeIcon: Record<string, typeof IconPhoto> = {
  Image: IconPhoto,
  Audio: IconMicrophone,
  Text: IconFileText,
};

export default function SelectPreset() {
  const presetService = useAiWorkflowPresetService();
  const { selectPreset } = useAiWorkflowStore();

  const { data: presets, isLoading, error } = useQuery({
    queryKey: ['ai', 'workflow-presets', 'system'],
    queryFn: () => presetService.getSystemPresets(),
  });

  if (isLoading) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack align="center" py="xl">
          <Loader size="md" />
          <Text c="dimmed">Loading presets...</Text>
        </Stack>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error loading presets" color="red">
        {error instanceof Error ? error.message : 'Failed to load presets'}
      </Alert>
    );
  }

  if (!presets || presets.length === 0) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="No presets available" color="yellow">
        No AI workflow presets have been configured yet. Contact your administrator.
      </Alert>
    );
  }

  const activePresets = presets.filter(p => p.isActive);

  return (
    <Stack gap="lg">
      {/* Section header */}
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="sm">
          <Group gap="xs">
            <IconSparkles size={20} />
            <Title order={4}>Select a Preset</Title>
          </Group>
          <Badge variant="light" color="violet">{activePresets.length} available</Badge>
        </Group>
        <Divider mb="sm" />
        <Text size="sm" c="dimmed">
          Choose the type of record you want to create from your input. Each preset defines
          the input type (image, audio, or text) and the target record type.
        </Text>
      </Card>

      {/* Preset cards in Grid */}
      <Grid>
        {activePresets.map((preset) => {
          const PresetIcon = inputTypeIcon[preset.inputType] ?? IconSparkles;
          const iconColor = inputTypeBadgeColor[preset.inputType] ?? 'gray';

          return (
            <Grid.Col key={preset.presetID} span={{ base: 12, sm: 6 }}>
              <Card
                shadow="sm"
                padding="lg"
                radius="md"
                withBorder
                style={{ cursor: 'pointer' }}
                onClick={() => selectPreset(
                  preset.presetID,
                  preset.name,
                  preset.inputType,
                  preset.targetRecordType
                )}
              >
                <Group gap="md" mb="sm">
                  <ThemeIcon size="lg" radius="md" variant="light" color={iconColor}>
                    <PresetIcon size={20} />
                  </ThemeIcon>
                  <Text fw={600} size="md">{preset.name}</Text>
                </Group>
                {preset.description && (
                  <Text size="sm" c="dimmed" lineClamp={2} mb="sm">
                    {preset.description}
                  </Text>
                )}
                <Group gap="xs">
                  <Badge
                    size="sm"
                    variant="light"
                    color={inputTypeBadgeColor[preset.inputType] ?? 'gray'}
                  >
                    {preset.inputType}
                  </Badge>
                  <Badge size="sm" variant="outline" color="gray">
                    {preset.targetRecordType}
                  </Badge>
                </Group>
              </Card>
            </Grid.Col>
          );
        })}
      </Grid>
    </Stack>
  );
}
